import { createMemo, createSignal } from "solid-js"
import type { SetStoreFunction } from "solid-js/store"
import { createJSONStorage, sessionStorageSource } from "@/storage/browser"
import { DESKTOP_PANEL_MIN_WIDTH, DESKTOP_PANEL_WIDTH } from "../layout-model"
import type { LayoutState } from "./state"

/**
 * Disposable cache: whether this tab shows the machine desktop panel. It
 * belongs to what this tab is watching, so only this tab's reloads keep it;
 * every other tab, including one this tab opens, starts with it closed.
 */
const tabOpened = createJSONStorage<boolean>(
  "mux.layout.desktop-panel.opened.v1",
  { decode: (value) => value === true },
  sessionStorageSource,
)

/** The machine desktop panel, shown beside the right sidebar rather than as one of its tabs. */
export function createLayoutDesktopPanel(store: LayoutState, setStore: SetStoreFunction<LayoutState>) {
  const [opened, setOpened] = createSignal(tabOpened.read(false))
  const update = (next: boolean) => {
    setOpened(next)
    tabOpened.write(next, { removeWhen: (value) => !value })
  }
  const width = createMemo(() => {
    const saved = store.desktop.width
    return typeof saved === "number" && Number.isFinite(saved) ? Math.max(DESKTOP_PANEL_MIN_WIDTH, saved) : DESKTOP_PANEL_WIDTH
  })

  return {
    opened,
    width,
    open: () => update(true),
    close: () => update(false),
    toggle: () => update(!opened()),
    resize(width: number) {
      setStore("desktop", "width", width)
    },
  }
}
